import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Activity, Film, UserCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge, statusTone } from '@/components/ui/badge';
import { StatCard } from '@/components/overview/StatCard';
import { api } from '@/lib/api';
import { archetypeLabel, formatRelative, shortId } from '@/lib/format';
import {
  OVERVIEW_RECENT_TAKE,
  OVERVIEW_RUNS_LAST_HOURS,
  OVERVIEW_RUNS_LIMIT,
  OVERVIEW_RUNS_POLL_MS,
} from '@/constants/overview';

export function OverviewPage() {
  const personasQ = useQuery({ queryKey: ['personas'], queryFn: () => api.listPersonas() });
  const contentQ = useQuery({ queryKey: ['content'], queryFn: () => api.listContent() });
  const runsQ = useQuery({
    queryKey: ['runs', 'recent'],
    queryFn: () =>
      api.listWorkflows({ last_hours: OVERVIEW_RUNS_LAST_HOURS, limit: OVERVIEW_RUNS_LIMIT }),
    refetchInterval: OVERVIEW_RUNS_POLL_MS,
  });

  const running = runsQ.data?.filter((r) => r.status === 'RUNNING') ?? [];
  const failed = runsQ.data?.filter((r) => r.status === 'FAILED') ?? [];

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>
        <p className="text-sm text-muted-foreground">
          All data backed by database. Polls every 10 seconds.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Personas"
          value={personasQ.data?.length}
          loading={personasQ.isLoading}
          icon={<UserCircle2 className="h-4 w-4" />}
          to="/personas"
        />
        <StatCard
          title="Content pieces"
          value={contentQ.data?.length}
          loading={contentQ.isLoading}
          icon={<Film className="h-4 w-4" />}
          to="/content"
        />
        <StatCard
          title="Running"
          value={running.length}
          loading={runsQ.isLoading}
          icon={<Activity className="h-4 w-4" />}
          to="/runs"
        />
        <StatCard
          title="Failed (24h)"
          value={failed.length}
          loading={runsQ.isLoading}
          icon={<Activity className="h-4 w-4" />}
          to="/runs"
          tone={failed.length > 0 ? 'failure' : 'neutral'}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>In-flight</CardTitle>
          <CardDescription>
            Workflows currently running on the `ghost-python` queue.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {runsQ.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-9" />
              <Skeleton className="h-9" />
            </div>
          ) : running.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing running.</p>
          ) : (
            <ul className="divide-y divide-border">
              {running.map((r) => (
                <li key={r.workflow_id} className="flex items-center justify-between py-2">
                  <Link
                    to="/runs/$workflowId"
                    params={{ workflowId: r.workflow_id }}
                    className="font-mono text-sm hover:underline"
                  >
                    {shortId(r.workflow_id)}
                  </Link>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{r.workflow_type}</span>
                    <Badge tone={statusTone(r.status)}>{r.status}</Badge>
                    <span>{formatRelative(r.start_time)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Recent personas</CardTitle>
          </CardHeader>
          <CardContent>
            {(personasQ.data ?? []).slice(0, OVERVIEW_RECENT_TAKE).map((p) => (
              <Link
                key={p.persona_id}
                to="/personas/$personaId"
                params={{ personaId: p.persona_id }}
                className="flex items-center justify-between py-1.5 text-sm hover:underline"
              >
                <span className="truncate">{p.voice_sheet?.display_name ?? '—'}</span>
                <span className="text-xs text-muted-foreground">{archetypeLabel(p.archetype)}</span>
              </Link>
            ))}
            {personasQ.data?.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No personas yet — try{' '}
                <Link to="/triggers/persona" className="underline">
                  triggering one
                </Link>
                .
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent content</CardTitle>
          </CardHeader>
          <CardContent>
            {(contentQ.data ?? []).slice(0, OVERVIEW_RECENT_TAKE).map((c) => (
              <Link
                key={c.content_id}
                to="/content/$contentId"
                params={{ contentId: c.content_id }}
                className="flex items-center justify-between py-1.5 text-sm hover:underline"
              >
                <span className="truncate">{c.script?.caption?.slice(0, 60) ?? c.content_id}</span>
                <span className="text-xs text-muted-foreground">{c.video?.provider_model}</span>
              </Link>
            ))}
            {contentQ.data?.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No content yet — pick a persona and{' '}
                <Link to="/triggers/content" className="underline">
                  generate
                </Link>
                .
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
